import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMediaQueryContext } from '../../contexts/MediaQueryContext'
import { useSearchMentors } from '../../hooks/apiHooks/searchHooks'
import { useSession } from '../../hooks/storeHooks/useSessionState'
import { useDebounce } from '../../hooks/useDebounce'
import { MentorBase } from '../../types/mentor'
import { analytics } from '../../utils/analytics'
import { OutOfMinutesModal } from '../Modals/OutOfMinutesModal'
import QuestionFormDesktop from './QuestionFormDesktop'
import { QuestionFormMobile } from './QuestionFormMobile'

export interface IQuestionFormProps {
  question: string
  handleAskQuestion: (q?: string) => void
  clearForm: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isDisabled: boolean
  suggestedCompanions?: MentorBase[]
  isFetching: boolean
  showSuggestions: boolean
  onFocus: () => void
}
interface LocationState {
  prevPage?: string
  cardId?: string
  showOutOfMinutesModal?: boolean
}

const PAGE_SIZE = 18

const QuestionForm: React.FC = () => {
  const queryClient = useQueryClient()
  const { isMd } = useMediaQueryContext()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const location = useLocation()
  const locationState = location.state as LocationState
  const [searchParams] = useSearchParams()
  const queryParam = searchParams.get('query') || ''
  const [question, setQuestion] = useState(queryParam)
  const { secondsBalance } = useSession()
  const [showOutOfMinutesModal, setShowOutOfMinutesModal] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const debouncedSearchQuery = useDebounce(question, 500)
  const secondsBalanceRef = useRef<number>(null)
  const [displayedResults, setDisplayedResults] = useState<MentorBase[]>()

  useEffect(() => {
    setIsDirty(false)
  }, [location.pathname])

  useEffect(() => {
    secondsBalanceRef.current = secondsBalance
  }, [secondsBalance])

  const { data: searchResult, isFetching } = useSearchMentors(
    'search-mentors',
    {
      search: debouncedSearchQuery.trim(),
      size: PAGE_SIZE,
    },
    Boolean(debouncedSearchQuery.trim()),
  )

  useEffect(() => {
    setDisplayedResults(searchResult)
  }, [searchResult])

  const handleClickOutOfMinutesModal = () => {
    setShowOutOfMinutesModal(false)
  }

  useEffect(() => {
    if (queryParam !== question) {
      setQuestion(queryParam)
      void queryClient.resetQueries({
        queryKey: ['search-mentors'],
        exact: false,
      })
    }
  }, [queryParam])

  const navigateToPreviousPage = () => {
    const prev = locationState?.prevPage || '/'
    const url = new URL(prev, window.location.origin)
    navigate(url.pathname === '/search' ? '/' : prev)
  }

  useEffect(() => {
    const showModal = locationState?.showOutOfMinutesModal
    if (showModal) {
      setShowOutOfMinutesModal(true)
      navigate(location.pathname, {
        replace: true,
        state: {
          ...locationState,
          showOutOfMinutesModal: false,
        },
      })
    }
  }, [location])

  useEffect(() => {
    if (!question.trim()) {
      setIsDirty(false)
    }
  }, [question])

  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      void queryClient.resetQueries({
        queryKey: ['search-mentors'],
        exact: false,
      })
    }
  }, [debouncedSearchQuery])

  const handleClear = () => {
    setQuestion('')
    setIsDirty(false)
    setDisplayedResults(undefined)
    if (location.pathname === '/search') {
      navigateToPreviousPage()
    }
  }

  const handleAskQuestion = (suggestedQ?: string) => {
    const q = suggestedQ || question.trim()
    if (!isAuthenticated) {
      setShowOutOfMinutesModal(true)
      setQuestion(q)
      return
    }
    if (
      secondsBalanceRef.current &&
      secondsBalanceRef.current <= 0 &&
      isAuthenticated
    ) {
      setQuestion(q)
      setShowOutOfMinutesModal(true)
      return
    }
    setIsDirty(false)

    if (!q || q === queryParam) return
    navigate(`/search?query=${encodeURIComponent(q)}`, {
      state: {
        prevPage:
          locationState?.prevPage || `${location.pathname}${location.search}`,
      },
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setQuestion(value)
    setIsDirty(true)
  }

  const handleFocus = () => {
    analytics.logEvent('aa_question_activated')
  }

  if (locationState?.cardId) {
    return null
  }

  return (
    <>
      {isMd ? (
        <QuestionFormMobile
          onChange={handleChange}
          question={question}
          clearForm={handleClear}
          handleAskQuestion={handleAskQuestion}
          suggestedCompanions={searchResult}
          isDisabled={!question.trim() || question === queryParam}
          isFetching={isFetching}
          showSuggestions={!!searchResult && searchResult.length > 0 && isDirty}
          onFocus={handleFocus}
        />
      ) : (
        <QuestionFormDesktop
          onChange={handleChange}
          question={question}
          clearForm={handleClear}
          handleAskQuestion={handleAskQuestion}
          suggestedCompanions={displayedResults}
          isDisabled={!question.trim() || question === queryParam}
          isFetching={isFetching}
          showSuggestions={
            !!displayedResults && displayedResults.length > 0 && isDirty
          }
          onFocus={handleFocus}
        />
      )}
      {showOutOfMinutesModal && (
        <OutOfMinutesModal
          onClose={handleClickOutOfMinutesModal}
          question={question}
        />
      )}
    </>
  )
}

export { QuestionForm }
